import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import { LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import { COLOR_PRIMARY_2, COLOR_TEXT_2, COLOR_GREY_200 } from 'src/utils/colors';

const CHART_HEIGHT = 280;
const BAR_COLOR = '#0097B2';

// Bucket order for each chart (match backend keys / frontend labels)
const OCCUPANCY_RANGE_ORDER = [
  '0-10%',
  '10-20%',
  '20-30%',
  '30-40%',
  '40-50%',
  '50-60%',
  '60-70%',
  '70-80%',
  '80-90%',
  '90-100%',
];
const ADR_RANGE_ORDER = [
  '$0-$50',
  '$50-100',
  '$100-150',
  '$150-$200',
  '$200-$250',
  '$250-$300',
  '$300-$400',
  '$400-$600',
  '$600-$1,000',
  '$1,000+',
];
const REVENUE_RANGE_ORDER = [
  '<$5k',
  '$5k-10k',
  '$10-20k',
  '$20-40k',
  '$40-60k',
  '$60-80k',
  '$80-100k',
  '$100-200k',
  '$200-300k',
  '$300k+',
];
const ACTIVE_DAYS_RANGE_ORDER = [
  '60-120',
  '120-180',
  '180-240',
  '240-300',
  '300-365',
];

/** Mock distributions when isUserAllowed is false (not-allowed users see chart shape only). */
const MOCK_OCCUPANCY = [
  { range: '0-10%', count: 42 },
  { range: '10-20%', count: 88 },
  { range: '20-30%', count: 156 },
  { range: '30-40%', count: 224 },
  { range: '40-50%', count: 312 },
  { range: '50-60%', count: 398 },
  { range: '60-70%', count: 365 },
  { range: '70-80%', count: 278 },
  { range: '80-90%', count: 192 },
  { range: '90-100%', count: 95 },
];
const MOCK_ADR = [
  { range: '$0-$50', count: 65 },
  { range: '$50-100', count: 218 },
  { range: '$100-150', count: 445 },
  { range: '$150-$200', count: 382 },
  { range: '$200-$250', count: 248 },
  { range: '$250-$300', count: 156 },
  { range: '$300-$400', count: 72 },
  { range: '$400-$600', count: 48 },
  { range: '$600-$1,000', count: 24 },
  { range: '$1,000+', count: 12 },
];
const MOCK_REVENUE = [
  { range: '<$5k', count: 88 },
  { range: '$5k-10k', count: 142 },
  { range: '$10-20k', count: 268 },
  { range: '$20-40k', count: 325 },
  { range: '$40-60k', count: 278 },
  { range: '$60-80k', count: 198 },
  { range: '$80-100k', count: 124 },
  { range: '$100-200k', count: 95 },
  { range: '$200-300k', count: 52 },
  { range: '$300k+', count: 28 },
];
const MOCK_ACTIVE_DAYS = [
  { range: '60-120', count: 198 },
  { range: '120-180', count: 312 },
  { range: '180-240', count: 385 },
  { range: '240-300', count: 278 },
  { range: '300-365', count: 402 },
];

/** Margins when showing blurred chart (axes remain visible). */
const BLUR_CHART_MARGIN = { top: 24, right: 24, left: 50, bottom: 30 };
const blurOverlaySx = {
  position: 'absolute' as const,
  top: BLUR_CHART_MARGIN.top,
  left: BLUR_CHART_MARGIN.left,
  right: BLUR_CHART_MARGIN.right,
  bottom: BLUR_CHART_MARGIN.bottom,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  pointerEvents: 'none' as const,
};

const axisTickStyle = {
  fontSize: 12,
  fontFamily: 'Roboto',
  fill: COLOR_TEXT_2,
  fontWeight: 500,
};

const CountTooltip = ({
  active,
  payload,
  labelKey = 'range',
  valueKey = 'count',
  suffix = ' listings',
  isUserAllowed: allowed = true,
}: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (!allowed) {
    return (
      <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon sx={{ fontSize: '1rem', color: COLOR_TEXT_2 }} />
        <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
          {p[labelKey]}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {p[labelKey]}: {p[valueKey]}
        {suffix}
      </Typography>
    </Box>
  );
};

function mapDistributionToData(
  dist: Record<string, number> | undefined,
  rangeOrder: string[],
): Array<{ range: string; count: number }> {
  if (!dist || typeof dist !== 'object') {
    return rangeOrder.map((range) => ({ range, count: 0 }));
  }
  return rangeOrder.map((range) => ({
    range,
    count: Number((dist as Record<string, number>)[range]) || 0,
  }));
}

export interface ListingsPerformanceChartsProps {
  isMobileView?: boolean;
  cachedMarketData?: any;
  currency?: string;
  /** When data is empty, CTA calls this to trigger hard re-cache (listings only); caller schedules refetches at 10s, 20s, 30s. */
  onRequestHardRefresh?: () => void;
  /** When false (not-allowed user), show mock data with blur overlay and lock tooltips (same as "When Do Guests Book"). */
  isUserAllowed?: boolean;
}

const ListingsPerformanceCharts: React.FC<ListingsPerformanceChartsProps> = ({
  isMobileView = false,
  cachedMarketData,
  currency = 'USD',
  onRequestHardRefresh,
  isUserAllowed = true,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const supplyBreakdown = cachedMarketData?.data?.supply_breakdown;
  const loading = cachedMarketData?.loading;

  const occupancyData = React.useMemo(() => {
    if (!isUserAllowed) return MOCK_OCCUPANCY;
    return mapDistributionToData(
      supplyBreakdown?.occupancy_distribution,
      OCCUPANCY_RANGE_ORDER,
    );
  }, [supplyBreakdown?.occupancy_distribution, isUserAllowed]);
  const adrData = React.useMemo(() => {
    if (!isUserAllowed) return MOCK_ADR;
    return mapDistributionToData(supplyBreakdown?.adr_distribution, ADR_RANGE_ORDER);
  }, [supplyBreakdown?.adr_distribution, isUserAllowed]);
  const revenueData = React.useMemo(() => {
    if (!isUserAllowed) return MOCK_REVENUE;
    return mapDistributionToData(
      supplyBreakdown?.revenue_distribution,
      REVENUE_RANGE_ORDER,
    );
  }, [supplyBreakdown?.revenue_distribution, isUserAllowed]);
  const activeDaysData = React.useMemo(() => {
    if (!isUserAllowed) return MOCK_ACTIVE_DAYS;
    return mapDistributionToData(
      supplyBreakdown?.active_days_distribution,
      ACTIVE_DAYS_RANGE_ORDER,
    );
  }, [supplyBreakdown?.active_days_distribution, isUserAllowed]);

  const showUsdNote = currency !== 'USD' && currency != null;

  const isEmpty =
    !cachedMarketData?.data ||
    !supplyBreakdown ||
    ([occupancyData, adrData, revenueData, activeDaysData].every((arr) =>
      arr.every((d) => d.count === 0),
    ));

  // When empty, use mock data so chart shape is visible under blur overlay
  const occupancyDisplayData = isEmpty ? MOCK_OCCUPANCY : occupancyData;
  const adrDisplayData = isEmpty ? MOCK_ADR : adrData;
  const revenueDisplayData = isEmpty ? MOCK_REVENUE : revenueData;
  const activeDaysDisplayData = isEmpty ? MOCK_ACTIVE_DAYS : activeDaysData;

  useEffect(() => {
    if (!isEmpty) setIsRefreshing(false);
  }, [isEmpty]);

  const handleRequestHardRefresh = () => {
    if (onRequestHardRefresh) {
      setIsRefreshing(true);
      onRequestHardRefresh();
    }
  };

  // After clicking "Load data", show loading for 30s then allow showing the button again if still empty
  useEffect(() => {
    if (!isRefreshing) return;
    const t = setTimeout(() => setIsRefreshing(false), 30000);
    return () => clearTimeout(t);
  }, [isRefreshing]);

  if (loading && isUserAllowed) {
    return (
      <Box sx={{ width: '100%' }}>
        <LoadingCard height={CHART_HEIGHT * 4 + 120} type="bars" />
      </Box>
    );
  }

  if (isEmpty && isRefreshing) {
    return (
      <Box sx={{ width: '100%' }}>
        <LoadingCard height={CHART_HEIGHT * 4 + 120} type="bars" />
      </Box>
    );
  }

  const chartMargin = isUserAllowed ? { top: 24, right: 24, left: 0, bottom: 20 } : BLUR_CHART_MARGIN;
  const yAxisLabel = {
    value: 'Listings',
    angle: -90,
    position: 'insideLeft' as const,
    style: { fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 },
  };

  const showEmptyOverlay = isEmpty && onRequestHardRefresh;

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* 1. Occupancy Rate */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: COLOR_PRIMARY_2,
            fontSize: '1rem',
            mb: 1.5,
          }}
        >
          Occupancy Rate
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={occupancyDisplayData}
                margin={chartMargin}
                barCategoryGap="10%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={COLOR_GREY_200}
                />
                <XAxis
                  dataKey="range"
                  axisLine={false}
                  tickLine={false}
                  tick={axisTickStyle}
                  angle={-35}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tickLine={false}
                  axisLine={{
                    stroke: COLOR_TEXT_2,
                    strokeDasharray: '4 4',
                  }}
                  tick={axisTickStyle}
                  label={yAxisLabel}
                />
                <Tooltip content={<CountTooltip isUserAllowed={isUserAllowed} />} cursor={false} />
                <Bar
                  dataKey="count"
                  fill={BAR_COLOR}
                  radius={[4, 4, 0, 0]}
                  barSize={24}
                  maxBarSize={36}
                />
              </BarChart>
            </ResponsiveContainer>
            {!isUserAllowed && !isEmpty && <Box sx={blurOverlaySx} />}
          </Box>
        </AIOverviewCard>
      </Box>

      {/* 2. Average Nightly Rate (fixed USD) */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: COLOR_PRIMARY_2,
            fontSize: '1rem',
            mb: 1.5,
          }}
        >
          Average Nightly Rate
        </Typography>
        {showUsdNote && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.5, color: COLOR_TEXT_2 }}
          >
            Values in USD
          </Typography>
        )}
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={adrDisplayData} margin={chartMargin} barCategoryGap="15%">
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={COLOR_GREY_200}
                />
                <XAxis
                  dataKey="range"
                  axisLine={false}
                  tickLine={false}
                  tick={axisTickStyle}
                />
                <YAxis
                  tickLine={false}
                  axisLine={{
                    stroke: COLOR_TEXT_2,
                    strokeDasharray: '4 4',
                  }}
                  tick={axisTickStyle}
                  label={yAxisLabel}
                />
                <Tooltip content={<CountTooltip isUserAllowed={isUserAllowed} />} cursor={false} />
                <Bar
                  dataKey="count"
                  fill={BAR_COLOR}
                  radius={[6, 6, 0, 0]}
                  barSize={32}
                  maxBarSize={48}
                />
              </BarChart>
            </ResponsiveContainer>
            {!isUserAllowed && !isEmpty && <Box sx={blurOverlaySx} />}
          </Box>
        </AIOverviewCard>
      </Box>

      {/* 3. Annual Revenue (fixed USD) */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: COLOR_PRIMARY_2,
            fontSize: '1rem',
            mb: 1.5,
          }}
        >
          Annual Revenue
        </Typography>
        {showUsdNote && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.5, color: COLOR_TEXT_2 }}
          >
            Values in USD
          </Typography>
        )}
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueDisplayData}
                margin={chartMargin}
                barCategoryGap="8%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={COLOR_GREY_200}
                />
                <XAxis
                  dataKey="range"
                  axisLine={false}
                  tickLine={false}
                  tick={axisTickStyle}
                  angle={-35}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tickLine={false}
                  axisLine={{
                    stroke: COLOR_TEXT_2,
                    strokeDasharray: '4 4',
                  }}
                  tick={axisTickStyle}
                  label={yAxisLabel}
                />
                <Tooltip content={<CountTooltip isUserAllowed={isUserAllowed} />} cursor={false} />
                <Bar
                  dataKey="count"
                  fill={BAR_COLOR}
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
            {!isUserAllowed && !isEmpty && <Box sx={blurOverlaySx} />}
          </Box>
        </AIOverviewCard>
      </Box>

      {/* 4. Listing Availability */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: COLOR_PRIMARY_2,
            fontSize: '1rem',
            mb: 1.5,
          }}
        >
          Listing Availability
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={activeDaysDisplayData}
                margin={chartMargin}
                barCategoryGap="25%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={COLOR_GREY_200}
                />
                <XAxis
                  dataKey="range"
                  axisLine={false}
                  tickLine={false}
                  tick={axisTickStyle}
                />
                <YAxis
                  tickLine={false}
                  axisLine={{
                    stroke: COLOR_TEXT_2,
                    strokeDasharray: '4 4',
                  }}
                  tick={axisTickStyle}
                  label={yAxisLabel}
                />
                <Tooltip content={<CountTooltip isUserAllowed={isUserAllowed} />} cursor={false} />
                <Bar
                  dataKey="count"
                  fill={BAR_COLOR}
                  radius={[6, 6, 0, 0]}
                  barSize={40}
                  maxBarSize={56}
                />
              </BarChart>
            </ResponsiveContainer>
            {!isUserAllowed && !isEmpty && <Box sx={blurOverlaySx} />}
          </Box>
        </AIOverviewCard>
      </Box>

      {/* Single overlay when empty: blur + Load data for all users */}
      {showEmptyOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fffa',
            backgroundBlendMode: 'multiply',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            New feature: Click to load this data. Processing may take 3–5 minutes, then refresh.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRequestHardRefresh}
            sx={{ textTransform: 'none' }}
          >
            Load data
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ListingsPerformanceCharts;
